import React, { useState } from 'react';
import { Button, Combobox, STATUS_TONE_CLASSES, STATUS_TEXT_CLASSES } from '@aidkitorg/component-library';
import { usePost } from '../API';
import { useDebouncedCallback } from '../Hooks/Debounce';
import type { FieldDiagnosis as FieldDiagnosisResult, FieldGate } from '@aidkitorg/types/lib/fieldDiagnosis';

// Semantic callout styles built from the component-library's shared design tokens (see Badge.tsx),
// so this panel matches the rest of the admin panel visually.
const NEUTRAL = STATUS_TONE_CLASSES.neutral;
const ERROR = STATUS_TONE_CLASSES.error;
const SUCCESS = STATUS_TONE_CLASSES.success;
const ERROR_TEXT = STATUS_TEXT_CLASSES.error;
const SUCCESS_TEXT = STATUS_TEXT_CLASSES.success;

// ApplicantPage auto-selects a subsurvey by patching the URL with a raw `window.history.replaceState`
// (see ApplicantPage.tsx) whenever a caseworker lands on the un-suffixed `/a/:uid` route -- that
// bypasses React Router entirely, so `useParams()` never observes the redirect and reads back
// undefined on the most common landing path. Parse the live URL directly instead of trusting the
// router's params, matching the `/a/:uid/:subsurvey` and `/ua/:dynamoAppId/:subsurvey` routes.
function currentSubsurveyFromLocation(pathname: string): string | undefined {
  const match = pathname.match(/^\/u?a\/[^/]+\/([^/]+)/);
  return match ? decodeURIComponent(match[1]) : undefined;
}

// A short one-line status for a diagnosis's collapsed row header, e.g. "FAILING — county_selected
// equals Mercer" -- the blocking gate's own summary if it's failing, otherwise a generic verdict.
function shortReason(diagnosis: Extract<FieldDiagnosisResult, { found: true }>): string {
  if (diagnosis.blockedAtGateIndex !== null) {
    return diagnosis.gates[diagnosis.blockedAtGateIndex].englishSummary;
  }
  if (diagnosis.gates.length === 0) {
    return 'always eligible to compute (no gates)';
  }
  return 'all gates passed';
}

function DiagnosisRow(props: { diagnosis: Extract<FieldDiagnosisResult, { found: true }>, currentSubsurvey?: string }) {
  const { diagnosis } = props;
  const [expanded, setExpanded] = useState(false);
  const isFailing = diagnosis.blockedAtGateIndex !== null;
  const isCurrentSection = !!props.currentSubsurvey && diagnosis.subsurveyPath === props.currentSubsurvey;

  return (
    <div className={`rounded my-1 ${isFailing ? ERROR : SUCCESS}`}>
      <button
        type="button"
        className="w-full text-left p-2 flex items-center gap-2"
        onClick={() => setExpanded(e => !e)}
      >
        <span>{expanded ? '▾' : '▸'}</span>
        <span className="font-semibold">{diagnosis.componentKind}</span>
        <span>{isFailing ? 'FAILING' : 'PASSING'}</span>
        <span className="font-mono text-xs truncate">{shortReason(diagnosis)}</span>
        {diagnosis.authoritative && (
          <span className="ml-auto text-xs px-1 rounded bg-[var(--ak-identity-color-off-white)]" title="This is the first Computed or Validated definition of this field -- it's the one the survey actually uses to compute the applicant's stored value.">used to compute value</span>
        )}
        {isCurrentSection && (
          <span className="text-xs px-1 rounded bg-[var(--ak-identity-color-off-white)]">current section</span>
        )}
        {diagnosis.subsurveyPath && (
          <span className="text-xs text-[var(--ak-identity-color-gray)]">in {diagnosis.subsurveyPath}</span>
        )}
        {diagnosis.hidden && <span className="text-xs">(hidden)</span>}
      </button>

      {expanded && (
        <div className="text-sm p-2 pt-0">
          {diagnosis.gates.length === 0 && (
            <div className="mt-1">No Conditional Block or conditionalOn gates this field — it's always eligible to compute.</div>
          )}

          {diagnosis.gates.length > 0 && (
            <ol className="mt-2 list-decimal list-inside">
              {diagnosis.gates.map((gate: FieldGate, i: number) => {
                const isBlocker = diagnosis.blockedAtGateIndex === i;
                return (
                  <li key={i} className={`p-2 my-1 rounded ${isBlocker ? ERROR : gate.passed ? SUCCESS : NEUTRAL}`}>
                    <div className="font-mono text-xs">{gate.englishSummary}</div>
                    <div className={gate.passed ? SUCCESS_TEXT : ERROR_TEXT}>
                      {gate.passed ? 'PASSED' : 'FAILED'}{isBlocker ? ' — this is the first gate blocking the field' : ''}
                    </div>
                    {/* When the gate is an And/Or/Not tree, explainCondition recurses into every leaf
                        clause -- surface exactly which ones failed rather than only the overall verdict. */}
                    {!gate.passed && gate.explanation.failed && gate.explanation.failed.length > 0 && (
                      <div className="mt-1">
                        <div>Failing clause(s):</div>
                        <ul className={`list-disc list-inside ${ERROR_TEXT}`}>
                          {gate.explanation.failed.map((clause, j) => (
                            <li key={j} className="font-mono text-xs">{clause}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {gate.explanation.relatedTargetFields && (
                      <div>
                        {Object.entries(gate.explanation.relatedTargetFields).map(([field, val]) => (
                          <div key={field}><var>{field}</var> = <data>{val === null ? '(unset)' : String(val)}</data></div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ol>
          )}

          {diagnosis.blockedAtGateIndex === null && diagnosis.gates.length > 0 && (
            <div className={`mt-2 ${SUCCESS_TEXT}`}>All gates passed — field is eligible to compute.</div>
          )}

          {diagnosis.formula && (
            <div className="mt-2">
              <strong>Formula:</strong>
              <pre className={`p-2 rounded text-xs overflow-x-auto ${NEUTRAL}`}>{diagnosis.formula}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FieldDiagnosis(props: { uid: string }) {
  const diagnoseField = usePost('/applicant/diagnose_field');
  const searchFields = usePost('/applicant/search_fields');
  const currentSubsurvey = currentSubsurveyFromLocation(window.location.pathname);
  const [fieldOptions, setFieldOptions] = useState<{ label: string, value: string }[]>([]);
  const [targetField, setTargetField] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<Awaited<ReturnType<typeof diagnoseField>> | null>(null);

  // Server-side substring search against `info_defs` (one row per field, indexed by key) -- scales
  // to programs with thousands of fields, unlike shipping the whole field list to the browser
  // (which was freezing the tab trying to filter/render it client-side on every keystroke).
  const debouncedSearch = useDebouncedCallback(async (term: string) => {
    if (term.trim().length < 2) {
      setFieldOptions([]);
      return;
    }
    const keys = await searchFields({ term: term.trim() });
    if (Array.isArray(keys)) {
      setFieldOptions(keys.map((field: string) => ({ label: field, value: field })));
    }
  }, 250);

  async function run() {
    if (!targetField.trim()) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await diagnoseField({ uid: props.uid, targetField: targetField.trim() });
      setResult(res);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={`rounded-md p-2 my-2 ${NEUTRAL}`}>
      <h3 className="text-xl font-bold">Why isn't this field computing?</h3>
      <div className="my-2 flex items-end gap-2">
        <div className="w-full max-w-full">
          <Combobox
            label="Target field"
            showLabel={false}
            multiple={false}
            portal={true}
            // sort/filterFunction intentionally left at "trust the server": /applicant/search_fields
            // already ranks prefix matches ("sflup_..." for a "sflup_" search) above fields that
            // merely contain the term elsewhere -- re-sorting/re-filtering client-side would undo that.
            allowFreeText={true}
            options={fieldOptions}
            value={targetField ? { label: targetField, value: targetField } : undefined}
            // Clear a stale diagnosis on selection change -- otherwise a failed diagnosis's error
            // message (which names the field it failed for) or a passing gate list can keep showing
            // after the admin picks a different field but before they click "Diagnose" again.
            onChange={(option) => { setTargetField(option?.value || ''); setResult(null); }}
            onQueryChange={debouncedSearch}
            filterFunction={() => true}
          />
        </div>
        <Button variant="primary" onClick={run} isDisabled={loading || !targetField.trim()} loading={loading}>
          Diagnose
        </Button>
      </div>

      {result && result.length === 1 && !result[0].found && (
        <div className={`p-2 my-2 rounded text-sm ${ERROR}`}>No component with targetField "{result[0].targetField}" was found anywhere in the live survey.</div>
      )}

      {result && result.length > 0 && result[0].found && (
        <div className="text-sm">
          {result.length > 1 && (
            <div className="mb-1 text-[var(--ak-identity-color-gray)]">
              This field is defined by {result.length} components — the one marked "used to compute value" is what actually runs.
            </div>
          )}
          {/* diagnoseField never mixes found/not-found entries -- a not-found result is always a
              lone `{found: false}` array, which the branch above already handled -- so every entry
              here is guaranteed `found: true`. */}
          {(result as Extract<FieldDiagnosisResult, { found: true }>[]).map((diagnosis, i) => (
            <DiagnosisRow key={i} diagnosis={diagnosis} currentSubsurvey={currentSubsurvey} />
          ))}
        </div>
      )}
    </div>
  );
}
